import { useState } from "react";

interface Profile {
  profile_id: string;
  backend_config: {
    collections: string[];
    prompt: Array<[string, string]>;
  };
}

interface AlertConfig {
  open: boolean;
  type: "success" | "error" | "loading";
  message: string;
}

export const useProfileUI = () => {
  const [profilesExpanded, setProfilesExpanded] = useState(true);
  const [adjustmentsExpanded, setAdjustmentsExpanded] = useState(true);
  const [instructionsExpanded, setInstructionsExpanded] = useState(true);

  const [editMode, setEditMode] = useState(false);
  const [profileToEdit, setProfileToEdit] = useState<Profile | null>(null);

  const [alertConfig, setAlertConfig] = useState<AlertConfig>({
    open: false,
    type: "success",
    message: "",
  });

  const handleEditProfile = (profile: Profile): void => {
    setEditMode(true);
    setProfileToEdit(profile);
  };

  const handleCancelEdit = (): void => {
    setEditMode(false);
    setProfileToEdit(null);
  };

  const exitEditMode = (): void => {
    setEditMode(false);
    setProfileToEdit(null);
  };

  const showAlert = (type: "success" | "error" | "loading", message: string): void => {
    setAlertConfig({
      open: true,
      type,
      message,
    });
  };

  const hideAlert = (): void => {
    setAlertConfig(prev => ({ ...prev, open: false }));
  };

  const showSuccessAlert = (message: string): void => {
    showAlert("success", message);
  };

  const showErrorAlert = (message: string): void => {
    showAlert("error", message);
  };

  const showLoadingAlert = (message: string): void => {
    showAlert("loading", message);
  };

  const toggleProfilesExpanded = (): void => {
    setProfilesExpanded(prev => !prev);
  };

  const toggleAdjustmentsExpanded = (): void => {
    setAdjustmentsExpanded(prev => !prev);
  };

  const toggleInstructionsExpanded = (): void => {
    setInstructionsExpanded(prev => !prev);
  };

  const resetUIStates = (): void => {
    setProfilesExpanded(true);
    setAdjustmentsExpanded(true);
    setInstructionsExpanded(true);
    setEditMode(false);
    setProfileToEdit(null);
    hideAlert();
  };

  const getEditInfo = () => {
    return {
      isEditing: editMode,
      profile: profileToEdit,
      hasProfile: profileToEdit !== null
    };
  };

  const hasCollapsedSections = (): boolean => {
    return !profilesExpanded || !adjustmentsExpanded || !instructionsExpanded;
  };

  const expandAllSections = (): void => {
    setProfilesExpanded(true);
    setAdjustmentsExpanded(true);
    setInstructionsExpanded(true);
  };

  const collapseAllSections = (): void => {
    setProfilesExpanded(false);
    setAdjustmentsExpanded(false);
    setInstructionsExpanded(false);
  };

  return {
    profilesExpanded,
    adjustmentsExpanded,
    instructionsExpanded,
    setProfilesExpanded,
    setAdjustmentsExpanded,
    setInstructionsExpanded,

    editMode,
    profileToEdit,
    setEditMode,
    setProfileToEdit,

    alertConfig,
    setAlertConfig,

    handleEditProfile,
    handleCancelEdit,
    exitEditMode,

    showAlert,
    hideAlert,
    showSuccessAlert,
    showErrorAlert,
    showLoadingAlert,

    toggleProfilesExpanded,
    toggleAdjustmentsExpanded,
    toggleInstructionsExpanded,

    resetUIStates,
    getEditInfo,
    hasCollapsedSections,
    expandAllSections,
    collapseAllSections
  };
};